import React, { Fragment, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import DownloadFileModal from '../../components/common/download-file-modal';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi from '../../utils/henceforthApi';
import moment from 'moment';
import BreadCrumb from '../../components/common/BreadCrumb';
import { numberWithCommas } from '../../utils/validations';
import { toast } from 'react-toastify';
import AgTable from '../../components/common/AgTable';
import { ColDef } from 'ag-grid-community';
import defaultImage from '../../assets/images/pages/defaultImage.jpg';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  Search,
  X,
  Download,
  Copy,
  Check,
  DollarSign,
  Calendar as CalendarIcon,
  RotateCcw,
  ChevronDown,
  Eye,
  Star,
  MessageSquare,
  ThumbsUp,
  TrendingUp,
  User,
  ShieldCheck,
  ShoppingBag,
} from 'lucide-react';
import SelectDropdown, { SelectDropdownOption } from '../../components/ui/select-dropdown';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';

const RatingReview: React.FC = () => {
  const Limit = 10;
  const location = useLocation();
  const navigate = useNavigate();
  const match = useMatch('/ratings/:page');
  const { authState, loading, setLoading, language_data, authDispatch, handleSearch, onFilterPriceHandler } =
    useContext(GlobalContext);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_rating_reviews || 'Ratings & Reviews'}`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const newParam = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [orderL, setOrderL] = useState<{ total_count: number; data: any[] }>({
    total_count: 0,
    data: [],
  });

  // Local Search state
  const [searchTerm, setSearchTerm] = useState(newParam.get('search') || '');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Selected review for preview modal
  const [selectedReview, setSelectedReview] = useState<any | null>(null);

  // Price popover state
  const [showPricePopover, setShowPricePopover] = useState(false);
  const [minPriceInput, setMinPriceInput] = useState(newParam.get('min_price') || '');
  const [maxPriceInput, setMaxPriceInput] = useState(newParam.get('max_price') || '');
  const pricePopoverRef = useRef<HTMLDivElement>(null);

  // Date popover state
  const [showDatePopover, setShowDatePopover] = useState(false);
  const datePopoverRef = useRef<HTMLDivElement>(null);

  // Rating score filter (client-side or query)
  const [ratingScoreFilter, setRatingScoreFilter] = useState<string>(newParam.get('rating_filter') || '');

  // Sync inputs with URL params
  useEffect(() => {
    setSearchTerm(newParam.get('search') || '');
    setMinPriceInput(newParam.get('min_price') || '');
    setMaxPriceInput(newParam.get('max_price') || '');
  }, [newParam]);

  // Click outside listeners
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pricePopoverRef.current && !pricePopoverRef.current.contains(e.target as Node)) {
        setShowPricePopover(false);
      }
      if (datePopoverRef.current && !datePopoverRef.current.contains(e.target as Node)) {
        setShowDatePopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const orderListing = async () => {
    setLoading(true);
    try {
      const pageNum = Number(match?.params.page) || 1;
      const res = (
        await henceforthApi.Order.getOrderRatingReview(pageNum - 1, Limit, newParam.toString())
      ).data;
      setOrderL({
        total_count: res.total_count || 0,
        data: res.data || [],
      });
    } catch (err: any) {
      console.log('err', err?.response?.body?.error_description);
      setOrderL({
        total_count: 0,
        data: [],
      });
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    orderListing();
  }, [
    match?.params.page,
    newParam.get('search'),
    newParam.get('product_id'),
    newParam.get('min_price'),
    newParam.get('max_price'),
    newParam.get('start_date'),
    newParam.get('end_date'),
  ]);

  const copyText = useCallback(
    (id: string, name: string) => {
      if (id) {
        navigator?.clipboard?.writeText(id);
        setCopiedId(id);
        toast.success(
          `${name} ${language_data?.[authState.lang]?.common_copy || 'copied'} ${language_data?.[authState.lang]?.common_successfull || 'successfully'
          }`
        );
        setTimeout(() => {
          setCopiedId((curr) => (curr === id ? null : curr));
        }, 2000);
      }
    },
    [authState, language_data]
  );

  const exportData = useCallback(
    async (startDate: number, endDate: number) => {
      try {
        const apiRes = await henceforthApi.Order.export(startDate, endDate, '&order_status=DELIVERED');
        const data = apiRes.data.data;
        const rows = [
          [
            'Sr.',
            'Order ID',
            'Customer Name',
            'Product ID',
            'Product Name',
            'Product Price',
            'Rating',
            'Review Description',
          ],
        ];
        if (Array.isArray(data)) {
          data.forEach((res: any, index: number) => {
            rows.push([
              String(index + 1),
              res?.order_id || 'N/A',
              res?.user_id?.name || 'N/A',
              res?.product_id?.prodct_id || 'N/A',
              res?.product_id?.name || 'N/A',
              String(res?.total_price || 0),
              String(res?.ratings || 0),
              res?.description || '',
            ]);
          });
        }
        const csvContent =
          'data:text/csv;charset=utf-8,' +
          rows.map((e) => e.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `ratings_reviews_${moment().valueOf()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const closeModal = document.getElementById('closeModal');
        if (closeModal) {
          closeModal.click();
        }
      } catch (err: any) {
        console.log(err?.response?.body?.error_description);
        handleError(err, 'active', authDispatch);
      }
    },
    [authDispatch]
  );

  // Search handler
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch('search', searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    handleSearch('search', '');
  };

  // Price handler
  const handleApplyPrice = (e: React.FormEvent) => {
    e.preventDefault();
    if (minPriceInput && maxPriceInput && Number(minPriceInput) > Number(maxPriceInput)) {
      toast.warn(
        `${language_data?.[authState.lang]?.common_max || 'Max'} price should be greater than ${language_data?.[authState.lang]?.common_min || 'Min'
        } price`
      );
      return;
    }
    onFilterPriceHandler(minPriceInput, maxPriceInput);
    setShowPricePopover(false);
  };

  const handleClearPrice = () => {
    setMinPriceInput('');
    setMaxPriceInput('');
    onFilterPriceHandler('', '');
    setShowPricePopover(false);
  };

  // Date handler
  const handleDateSelect = (val: any) => {
    if (Array.isArray(val) && val[0] && val[1]) {
      handleSearch('start_date', String(moment(val[0]).startOf('day').valueOf()));
      handleSearch('end_date', String(moment(val[1]).endOf('day').valueOf()));
      setShowDatePopover(false);
    }
  };

  const handleClearDate = () => {
    handleSearch('start_date', '');
    setShowDatePopover(false);
  };

  // Reset all filters
  const handleResetAllFilters = () => {
    setSearchTerm('');
    setMinPriceInput('');
    setMaxPriceInput('');
    setRatingScoreFilter('');
    navigate({
      pathname: '/ratings/1',
      search: '',
    });
  };

  const hasSearch = Boolean(newParam.get('search'));
  const hasPrice = Boolean(newParam.get('min_price') || newParam.get('max_price'));
  const hasDate = Boolean(newParam.get('start_date'));
  const isAnyFilterActive = hasSearch || hasPrice || hasDate || Boolean(ratingScoreFilter);

  // Quick stats calculation
  const stats = useMemo(() => {
    const total = orderL.total_count || 0;
    const currentList = orderL.data || [];
    if (!currentList.length) {
      return { avgScore: '0.0', positiveCount: 0, positivePct: 0, totalReviews: total };
    }
    const scoreSum = currentList.reduce((sum, item) => sum + (Number(item?.ratings) || 0), 0);
    const avgScore = (scoreSum / currentList.length).toFixed(1);
    const positiveCount = currentList.filter((item) => (Number(item?.ratings) || 0) >= 4).length;
    const positivePct = Math.round((positiveCount / currentList.length) * 100);
    return {
      avgScore,
      positiveCount,
      positivePct,
      totalReviews: total,
    };
  }, [orderL]);

  // Filter table rows by rating if score filter is set
  const filteredRowData = useMemo(() => {
    if (!ratingScoreFilter) return orderL.data;
    const targetScore = Number(ratingScoreFilter);
    return (orderL.data || []).filter((item) => Math.round(Number(item?.ratings) || 0) === targetScore);
  }, [orderL.data, ratingScoreFilter]);

  // AG Table Columns
  const columnDefs: ColDef[] = useMemo(() => {
    const pageNum = Number(match?.params.page) || 1;
    return [
      {
        headerName: '#',
        field: '_index',
        width: 65,
        minWidth: 55,
        sortable: false,
        filter: false,
        pinned: 'left',
        cellRenderer: (params: any) => {
          const index = (pageNum - 1) * Limit + (params.node?.rowIndex ?? 0) + 1;
          return (
            <div className="flex items-center h-full text-slate-400 font-bold text-xs">
              {index}
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_customer || 'Customer'}`,
        field: 'user_id.name',
        minWidth: 210,
        cellRenderer: (params: any) => {
          const user = params.data?.user_id;
          const pic = user?.profile_pic
            ? `${henceforthApi.API_FILE_ROOT_SMALL}${user.profile_pic}`
            : defaultImage;
          const name = user?.name || language_data?.[authState.lang]?.common_not_available || 'Customer';
          return (
            <div className="flex items-center gap-2.5 h-full py-1">
              <img
                src={pic}
                alt={name}
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
              />
              <div className="flex flex-col justify-center overflow-hidden">
                <span className="font-semibold text-slate-800 text-xs sm:text-sm truncate">
                  {name}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                  <ShieldCheck className="w-3 h-3 text-emerald-500" />
                  Verified Buyer
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_product || 'Product'}`,
        field: 'product_id.name',
        minWidth: 260,
        cellRenderer: (params: any) => {
          const product = params.data?.product_id;
          const pic = product?.images?.[0]
            ? `${henceforthApi.API_FILE_ROOT_SMALL}${product.images[0]}`
            : defaultImage;
          const category = product?.category_id?.name || '';
          const title = product?.name || 'Product Item';
          return (
            <div className="flex items-center gap-3 h-full py-1">
              <img
                src={pic}
                alt={title}
                className="w-9 h-9 rounded-lg object-cover border border-slate-200 shrink-0"
              />
              <div className="flex flex-col justify-center overflow-hidden">
                {category && (
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">
                    {category}
                  </span>
                )}
                <span className="text-xs font-semibold text-slate-800 truncate" title={title}>
                  {title}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_id || 'ID'
          }`,
        field: 'product_id.prodct_id',
        width: 140,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const code = params.data?.product_id?.prodct_id || '—';
          const isCopied = copiedId === code && code !== '—';
          return (
            <div className="flex items-center gap-1.5 h-full">
              <span className="font-mono text-xs font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200/60 truncate max-w-[90px]">
                {code}
              </span>
              {code !== '—' && (
                <button
                  type="button"
                  onClick={() => copyText(code, 'Product ID')}
                  className="p-1 text-slate-400 hover:text-slate-700 rounded transition-colors cursor-pointer"
                  title="Copy Product ID"
                >
                  {isCopied ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_price || 'Price'
          }`,
        field: 'product_id.discount_price',
        width: 110,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const price = params.data?.product_id?.discount_price ?? params.data?.total_price;
          return (
            <div className="flex items-center h-full font-black text-slate-900 text-xs sm:text-sm">
              ${price ? numberWithCommas(price) : '0'}
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_rating_reviews || 'Rating & Feedback'}`,
        field: 'ratings',
        minWidth: 280,
        cellRenderer: (params: any) => {
          const ratingVal = Number(params.data?.ratings) || 0;
          const description = params.data?.description || '';
          const title = params.data?.title || '';
          const scoreColor =
            ratingVal >= 4
              ? 'bg-amber-50 text-amber-700 border-amber-200'
              : ratingVal >= 3
                ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                : 'bg-rose-50 text-rose-700 border-rose-200';

          return (
            <div className="flex flex-col justify-center gap-1 h-full py-1.5">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-black border ${scoreColor}`}
                >
                  <Star className="w-3 h-3 fill-amber-400 text-amber-500" />
                  {ratingVal.toFixed(1)}
                </span>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-3 h-3 ${star <= Math.round(ratingVal)
                          ? 'fill-amber-400 text-amber-400'
                          : 'fill-slate-100 text-slate-200'
                        }`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5 overflow-hidden">
                {title && <span className="text-xs font-bold text-slate-800 truncate">{title}:</span>}
                <p className="text-xs text-slate-500 font-medium truncate m-0 flex-1">
                  {description || (
                    <span className="italic text-slate-400">No review comment provided</span>
                  )}
                </p>
                {description && description.length > 25 && (
                  <button
                    type="button"
                    onClick={() => setSelectedReview(params.data)}
                    className="text-[11px] text-[#1AA7DE] font-semibold hover:underline shrink-0 cursor-pointer"
                  >
                    Read
                  </button>
                )}
              </div>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_action || 'Action'}`,
        field: '_id',
        width: 120,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: 'right',
        cellRenderer: (params: any) => {
          const productId = params.data?.product_id?._id;
          const isBlocked =
            authState?.is_admin_verified === 'pending' || authState?.is_admin_verified === 'reject';
          return (
            <div className="flex items-center gap-1.5 h-full">
              <Button variant="default" size="sm" asChild>
                <Link
                  to={isBlocked ? '#' : `/product/${productId}`}
                  onClick={(e) => {
                    if (authState?.is_admin_verified === 'pending') {
                      e.preventDefault();
                      toast.error('Your account is pending for admin verification.');
                    } else if (authState?.is_admin_verified === 'reject') {
                      e.preventDefault();
                      toast.error('Your account is rejected for admin verification.');
                    }
                  }}
                  title="View Product Details"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">
                    {language_data?.[authState.lang]?.common_view || 'View'}
                  </span>
                </Link>
              </Button>
            </div>
          );
        },
      },
    ];
  }, [match?.params.page, Limit, language_data, authState, copiedId, copyText]);

  return (
    <Fragment>
      {/* Standard Breadcrumb Bar */}
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {/* 1. Header Card with Title, Counter Badge & Export Button */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_rating_reviews || 'Ratings & Reviews'}
              </h1>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200/60 shadow-2xs">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500 me-1" />
                {orderL.total_count} {orderL.total_count === 1 ? 'Review' : 'Reviews'}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
              Customer reviews, verified satisfaction scores, and feedback for your listed products.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Export CSV Button */}
            <button
              type="button"
              data-bs-toggle="modal"
              data-bs-target="#fileDownloadModal"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs sm:text-sm shadow-2xs transition-all active:scale-[0.98] cursor-pointer"
            >
              <Download className="w-4 h-4 text-slate-500" />
              <span>{language_data?.[authState.lang]?.common_export_file || 'Export CSV'}</span>
            </button>
          </div>
        </div>
        {/* 3. Modern Filter & Search Toolbar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-3.5">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 flex-wrap">
            {/* Search Input */}
            <form onSubmit={handleSearchSubmit} className="relative max-w-[360px] flex-1 min-w-[260px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search reviews by customer, product, or SKU..."
                className="w-full pl-10 pr-9 py-2 text-xs sm:text-sm font-medium bg-slate-50/80 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </form>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Rating Filter Dropdown */}
              <SelectDropdown
                heading="Rating Score"
                options={[
                  { value: '', label: 'All Ratings', subLabel: 'Show all review scores' },
                  { value: '5', label: '5 Stars ★★★★★', subLabel: 'Excellent satisfaction' },
                  { value: '4', label: '4 Stars ★★★★☆', subLabel: 'Very good' },
                  { value: '3', label: '3 Stars ★★★☆☆', subLabel: 'Average feedback' },
                  { value: '2', label: '2 Stars ★★☆☆☆', subLabel: 'Needs attention' },
                  { value: '1', label: '1 Star ★☆☆☆☆', subLabel: 'Critical feedback' },
                ]}
                value={ratingScoreFilter}
                onChange={(val) => setRatingScoreFilter(val)}
              />

              {/* Date Filter Popover */}
              <div className="relative" ref={datePopoverRef}>
                <button
                  type="button"
                  onClick={() => setShowDatePopover(!showDatePopover)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs sm:text-sm font-bold transition-all cursor-pointer ${hasDate
                      ? 'bg-sky-50 border-[#1AA7DE] text-[#1AA7DE]'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-2xs'
                    }`}
                >
                  <CalendarIcon className="w-4 h-4 text-slate-500" />
                  <span>
                    {hasDate
                      ? `${moment(Number(newParam.get('start_date'))).format('DD MMM')} - ${moment(
                        Number(newParam.get('end_date'))
                      ).format('DD MMM')}`
                      : 'Date Range'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>

                {showDatePopover && (
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 z-50 animate-in fade-in zoom-in-95">
                    <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Filter by Date
                      </span>
                      {hasDate && (
                        <button
                          type="button"
                          onClick={handleClearDate}
                          className="text-xs text-[#1AA7DE] font-semibold hover:underline cursor-pointer"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <Calendar
                      selectRange={true}
                      onChange={handleDateSelect}
                      className="border-0 rounded-xl shadow-none"
                    />
                  </div>
                )}
              </div>

              {/* Price Filter Popover */}
              <div className="relative" ref={pricePopoverRef}>
                <button
                  type="button"
                  onClick={() => setShowPricePopover(!showPricePopover)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs sm:text-sm font-bold transition-all cursor-pointer ${hasPrice
                      ? 'bg-sky-50 border-[#1AA7DE] text-[#1AA7DE]'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-2xs'
                    }`}
                >
                  <DollarSign className="w-4 h-4 text-slate-500" />
                  <span>
                    {hasPrice
                      ? `$${newParam.get('min_price') || '0'} - $${newParam.get('max_price') || '∞'}`
                      : 'Price Range'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>

                {showPricePopover && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 z-50 animate-in fade-in zoom-in-95">
                    <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Filter by Product Price
                      </span>
                      {hasPrice && (
                        <button
                          type="button"
                          onClick={handleClearPrice}
                          className="text-xs text-[#1AA7DE] font-semibold hover:underline cursor-pointer"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <form onSubmit={handleApplyPrice} className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[11px] font-bold text-slate-500 block mb-1">
                            Min ($)
                          </label>
                          <input
                            type="number"
                            value={minPriceInput}
                            onChange={(e) => setMinPriceInput(e.target.value)}
                            placeholder="0"
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-bold text-slate-500 block mb-1">
                            Max ($)
                          </label>
                          <input
                            type="number"
                            value={maxPriceInput}
                            onChange={(e) => setMaxPriceInput(e.target.value)}
                            placeholder="1000"
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                          />
                        </div>
                      </div>

                      {/* Quick presets */}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {[
                          { label: '$0 - $50', min: '0', max: '50' },
                          { label: '$50 - $200', min: '50', max: '200' },
                          { label: '$200 - $1000', min: '200', max: '1000' },
                        ].map((preset) => (
                          <button
                            key={preset.label}
                            type="button"
                            onClick={() => {
                              setMinPriceInput(preset.min);
                              setMaxPriceInput(preset.max);
                            }}
                            className="px-2 py-1 rounded text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                        <button
                          type="submit"
                          className="flex-1 py-1.5 rounded-lg bg-[#1AA7DE] hover:bg-[#158ebf] text-white font-bold text-xs shadow-2xs transition-colors cursor-pointer"
                        >
                          Apply Price
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowPricePopover(false)}
                          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-xs transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Reset All Filters button */}
              {isAnyFilterActive && (
                <button
                  type="button"
                  onClick={handleResetAllFilters}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-rose-200 text-xs sm:text-sm font-semibold text-rose-500 bg-white shadow-sm hover:shadow-md hover:border-rose-300 transition-all cursor-pointer"
                  title="Reset all filters"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 4. AG Grid Modern Table */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <AgTable
            rowData={filteredRowData || []}
            columnDefs={columnDefs}
            loading={loading}
            pagination={true}
            page={Number(match?.params.page) || 1}
            limit={Number(Limit)}
            count={orderL?.total_count}
            emptyMessage={
              language_data?.[authState.lang]?.common_no_data_found || 'No Reviews Found'
            }
            emptySubtitle="Customer reviews and ratings will appear here as soon as verified buyers submit feedback on your products."
          />
        </div>
      </div>

      {/* 5. Detailed Review Modal */}
      {selectedReview && (
        <Dialog open={Boolean(selectedReview)} onOpenChange={(open) => !open && setSelectedReview(null)}>
          <DialogContent className="max-w-lg bg-white rounded-2xl p-6 shadow-2xl">
            <DialogHeader className="border-b border-slate-100 pb-3">
              <DialogTitle className="text-lg font-black text-slate-900 flex items-center gap-2">
                <Star className="w-5 h-5 fill-amber-400 text-amber-500" />
                Customer Review Details
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Verified buyer feedback and satisfaction ratings
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              {/* Customer and Rating Banner */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 border border-slate-200/60">
                <div className="flex items-center gap-3">
                  <img
                    src={
                      selectedReview?.user_id?.profile_pic
                        ? `${henceforthApi.API_FILE_ROOT_SMALL}${selectedReview.user_id.profile_pic}`
                        : defaultImage
                    }
                    alt={selectedReview?.user_id?.name || 'Customer'}
                    className="w-10 h-10 rounded-full object-cover border border-slate-200"
                  />
                  <div>
                    <span className="font-bold text-slate-900 text-sm block">
                      {selectedReview?.user_id?.name || 'Customer'}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-semibold">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                      Verified Purchase
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-black bg-amber-50 text-amber-700 border border-amber-200">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
                    {Number(selectedReview?.ratings || 0).toFixed(1)} / 5.0
                  </span>
                  <div className="flex items-center gap-0.5 justify-end mt-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-3 h-3 ${s <= Math.round(Number(selectedReview?.ratings || 0))
                            ? 'fill-amber-400 text-amber-400'
                            : 'fill-slate-100 text-slate-200'
                          }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Product Info */}
              {selectedReview?.product_id && (
                <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white">
                  <img
                    src={
                      selectedReview.product_id.images?.[0]
                        ? `${henceforthApi.API_FILE_ROOT_SMALL}${selectedReview.product_id.images[0]}`
                        : defaultImage
                    }
                    alt={selectedReview.product_id.name || 'Product'}
                    className="w-12 h-12 rounded-lg object-cover border border-slate-200 shrink-0"
                  />
                  <div className="overflow-hidden flex-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      {selectedReview.product_id.category_id?.name || 'Product'}
                    </span>
                    <span className="font-bold text-slate-800 text-xs sm:text-sm truncate block">
                      {selectedReview.product_id.name}
                    </span>
                    <span className="text-xs font-semibold text-emerald-600 block">
                      ${numberWithCommas(selectedReview.product_id.discount_price || selectedReview.total_price || 0)}
                    </span>
                  </div>
                </div>
              )}

              {/* Review Text */}
              <div className="space-y-1.5 p-3.5 rounded-xl bg-slate-50/60 border border-slate-200/50">
                {selectedReview?.title && (
                  <h4 className="text-sm font-black text-slate-900 m-0">
                    "{selectedReview.title}"
                  </h4>
                )}
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed m-0 whitespace-pre-wrap">
                  {selectedReview?.description || 'No descriptive comment was left for this review.'}
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedReview(null)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* CSV Export Modal */}
      <DownloadFileModal exportData={exportData} />
    </Fragment>
  );
};

export default RatingReview;